import { CSSProperties, useState, useContext } from 'react';
import * as React from 'react';
import { Button, ButtonProps, Box, CircularProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { FormikContext } from 'formik';

type LoadingButtonProps = {
  isLoading: boolean;
  buttonWrapperClasses: string;
  progressVariant: 'determinate' | 'indeterminate';
  progressColor: 'primary' | 'secondary' | 'inherit';
  progressValue: number;
  progressSize: number;
  progressStyles: CSSProperties;
  disableFormikSubmitting: boolean;
};

const useStyles = makeStyles(() =>
  createStyles({
    buttonWrapper: ({ fullWidth }: { fullWidth: boolean }) => ({
      display: fullWidth ? 'block' : 'inline-block',
      position: 'relative',
    }),
    buttonProgress: {
      position: 'absolute',
      top: '45%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      height: 24,
    },
  })
);

/**
 * @summary Специальная кнопка с возможностью отображения загрузки внутри себя.
 *
 * @param isLoading - Флаг того, что нужно показывать загрузку.
 * @param buttonWrapperClasses - Дополнительные стили для обёртки кнопки.
 * @param progressVariant - Вариации загрузки, по умолчанию "indeterminate".
 * @param progressColor - Цвет индикатора загрузки (цвета из темы), по умолчанию
 * "secondary".
 * @param progressValue - Значение прогресса, число от 0 до 100. Если не передано
 * или равно нулю, то progressVariant интерпритируется, как "indeterminate".
 * @param progressSize - Размер индикатора прогресса, измеряется в пикселях. По
 * умолчанию 24 пикселя.
 * @param progressStyles - Стили для индикатора прогресса.
 * Все остальные свойства будут передаваться прямиком в кнопку.
 * @param disableFormikSubmitting - Отключает состояние loading от контекста формика.
 *
 * @description По умолчанию крутилка появляется на время выполнения функции onSubmit.
 *
 * @see https://material-ui.com/ru/api/circular-progress/
 */
export const LoadingButton: React.FC<ButtonProps & Partial<LoadingButtonProps>> = (props) => {
  const {
    children,
    isLoading = false,
    buttonWrapperClasses,
    progressVariant = 'indeterminate',
    progressColor = 'secondary',
    progressValue = 0,
    progressStyles,
    progressSize = 24,
    onClick,
    disabled,
    fullWidth,
    disableFormikSubmitting,
    ...rest
  } = props;
  const classes = useStyles({ fullWidth: Boolean(fullWidth) });
  const formik = useContext(FormikContext);
  const isFormikSubmitting = disableFormikSubmitting ? false : formik?.isSubmitting;

  const [isAsyncClick, setIsAsyncClick] = useState<boolean>(false);

  // случаи когда вызываем асинхронное действие в click
  // например onClick={async () => { await onClickHandler(); }}
  const onClickHandler = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (onClick === undefined || isAsyncClick) {
      return;
    }
    setIsAsyncClick(true);

    try {
      await onClick(event);
    } finally {
      setIsAsyncClick(false);
    }
  };

  return (
    <Box className={`${classes.buttonWrapper} ${buttonWrapperClasses}`}>
      <Button
        color="secondary"
        variant="contained"
        fullWidth={fullWidth}
        disabled={disabled || isFormikSubmitting || isLoading}
        onClick={(e) => onClickHandler(e)}
        {...rest}
      >
        {children}
      </Button>
      {(isFormikSubmitting || isLoading) && (
        <Box className={classes.buttonProgress}>
          <CircularProgress
            variant={progressVariant}
            color={progressColor}
            value={progressValue}
            style={{ ...progressStyles }}
            size={progressSize}
          />
        </Box>
      )}
    </Box>
  );
};
